/**
 * Client-side campaign verdicts over projected LTV:CAC credible intervals —
 * LOCKSTEP twin of builder-service `services/subscriptionLtv/verdict.ts`
 * (frontend cannot import backend src; the shared fixture table in
 * `__tests__/campaignVerdict.test.ts` pins both to the same behavior).
 *
 * The dashboard applies these thresholds LIVE: saving the gear popover
 * re-verdicts every row through this pure function without a payload refetch
 * (AC-011). Research reports freeze the thresholds they were generated with —
 * a stored report never re-verdicts (AC-013).
 *
 * Decision table (AC-006, strict inequalities per AC-020; kill before scale
 * so a misconfigured overlapping pair fails safe):
 *   organic / no CAC / missing quantiles → none
 *   cohortAge < minAge                   → too_young
 *   P90 < kill                           → kill
 *   P10 > scale                          → scale
 *   otherwise                            → watch
 */

export type CampaignVerdict = 'kill' | 'watch' | 'scale' | 'too_young' | 'none';

export interface CampaignVerdictThresholds {
  killBelowP90Roas: number;
  scaleAboveP10Roas: number;
  minCohortAgeDays: number;
}

export const DEFAULT_VERDICT_THRESHOLDS: CampaignVerdictThresholds = {
  killBelowP90Roas: 1.0,
  scaleAboveP10Roas: 1.2,
  minCohortAgeDays: 60,
};

export const MIN_COHORT_AGE_FLOOR_DAYS = 30;
export const MAX_COHORT_AGE_DAYS = 365;

export interface CampaignVerdictInput {
  isOrganic: boolean;
  cac: number | null;
  cohortAgeDays: number | null;
  pltv12RoasP10: number | null;
  pltv12RoasP90: number | null;
  /**
   * PD-3 (money-disclosure W-A, owner 2026-08-20): the projection basis
   * status feeding these quantiles. On 'partial' (boundary-shaped basis with
   * a pending tail) KILL may still fire — pending money can only add value,
   * so a P90 below breakeven is only strengthened — while SCALE stays gated
   * on the full 'available' basis. Absent means 'available' (back-compat).
   */
  basisStatus?: 'available' | 'partial';
}

export function computeCampaignVerdict(
  input: CampaignVerdictInput,
  thresholds: CampaignVerdictThresholds,
): CampaignVerdict {
  if (input.isOrganic) return 'none';
  if (input.cac === null || !Number.isFinite(input.cac) || input.cac <= 0) return 'none';
  if (input.cohortAgeDays === null || !Number.isFinite(input.cohortAgeDays)) return 'none';
  if (input.cohortAgeDays < thresholds.minCohortAgeDays) return 'too_young';
  const { pltv12RoasP10, pltv12RoasP90 } = input;
  if (
    pltv12RoasP10 === null ||
    pltv12RoasP90 === null ||
    !Number.isFinite(pltv12RoasP10) ||
    !Number.isFinite(pltv12RoasP90)
  ) {
    return 'none';
  }
  if (pltv12RoasP90 < thresholds.killBelowP90Roas) return 'kill';
  if (input.basisStatus === 'partial') {
    // PD-3: a scale-worthy signal on a partial basis makes no verdict claim
    // ('none', not 'watch' — 'watch' would assert "between thresholds").
    return pltv12RoasP10 > thresholds.scaleAboveP10Roas ? 'none' : 'watch';
  }
  if (pltv12RoasP10 > thresholds.scaleAboveP10Roas) return 'scale';
  return 'watch';
}

export type CampaignVerdictThresholdsError =
  | 'threshold_not_numeric'
  | 'threshold_not_positive'
  | 'scale_not_above_kill'
  | 'min_cohort_age_out_of_range';

/** Popover inline validation — same rule set the PATCH endpoint enforces. */
export function validateCampaignVerdictThresholds(
  thresholds: CampaignVerdictThresholds,
): CampaignVerdictThresholdsError | null {
  const { killBelowP90Roas, scaleAboveP10Roas, minCohortAgeDays } = thresholds;
  if (![killBelowP90Roas, scaleAboveP10Roas, minCohortAgeDays].every(Number.isFinite)) {
    return 'threshold_not_numeric';
  }
  if (killBelowP90Roas <= 0 || scaleAboveP10Roas <= 0) return 'threshold_not_positive';
  if (scaleAboveP10Roas <= killBelowP90Roas) return 'scale_not_above_kill';
  if (
    !Number.isInteger(minCohortAgeDays) ||
    minCohortAgeDays < MIN_COHORT_AGE_FLOOR_DAYS ||
    minCohortAgeDays > MAX_COHORT_AGE_DAYS
  ) {
    return 'min_cohort_age_out_of_range';
  }
  return null;
}

export const VERDICT_THRESHOLD_ERROR_COPY: Record<CampaignVerdictThresholdsError, string> = {
  threshold_not_numeric: 'Thresholds must be numbers.',
  threshold_not_positive: 'ROAS thresholds must be positive.',
  scale_not_above_kill: 'Scale threshold must be above the kill threshold.',
  min_cohort_age_out_of_range: `Minimum cohort age must be a whole number between ${MIN_COHORT_AGE_FLOOR_DAYS} and ${MAX_COHORT_AGE_DAYS} days.`,
};
